import getOr from 'lodash/fp/getOr';

import { reportErrorToSentry } from '../setup/sentry';

import { DEFAULT_LOCALE, getSupportedLocale as defaultGetSupportedLocale } from './lang';
import { changeLocale, displayMessagesFetched } from './langSlice';
import { trace } from '../setup/trace';
import type { Exception } from '@sentry/browser';
import type { Store } from '@reduxjs/toolkit';

const MODULE_NAME = 'tag-administration-web';

const sendError = (exception: Exception) => {
    reportErrorToSentry(exception, {
        tags: {
            module: MODULE_NAME,
        },
    });
};

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const normalizeDynamicImport = (imported: any) => getOr(imported, 'default', imported);

export const getLanguageData = (locale: string) =>
    import(/* webpackChunkName: "[request]" */ `../../features/translations/${locale}.json`)
        .then(normalizeDynamicImport)
        .catch(error => {
            sendError(error);
            return error;
        });

export const configureFetchDisplayMessages =
    (store: Store, fetchDisplayMessages = getLanguageData, getSupportedLocale = defaultGetSupportedLocale) =>
    async (locale: string) => {
        if (!locale) {
            console.warn(`No "locale" supplied when fetching language data!`);
            return Promise.reject();
        }

        const supportedLocale = getSupportedLocale(locale);
        try {
            const displayMessages = await fetchDisplayMessages(supportedLocale);
            trace(`Display messages fetched for "${supportedLocale}"`);
            store.dispatch(displayMessagesFetched({ locale: supportedLocale, displayMessages }));
        } catch (error: unknown) {
            console.error(`Display messages for "${supportedLocale}" could not be fetched.`, error);
            sendError(error as Exception);
            store.dispatch(changeLocale(DEFAULT_LOCALE));
        }
    };
